import { IconAlertTriangle, IconChevronRight } from "@tabler/icons-react";
import { useMemo } from "react";
import { createMemoryStore } from "../../../lib/store";
import { cn } from "../../../lib/utils";
import { useApiQuery } from "../../../services/api";
import {
  COMPOSITION_PARTS,
  agentAlerts,
  alertCategory,
  alertText,
  callCredits,
  compositionOverhead,
  emittedToolCalls,
  formatBytes,
  formatCount,
  formatCredits,
  formatMs,
  formatPercent,
  jsonTokens,
  roundAlerts,
  searchOutcomeText,
  type Alert,
  type JsonToken,
  type MissionSearch,
  type RowState,
  type RunDebug,
  type Trace,
  type TraceAgent,
  type TraceCall,
  type TraceRound,
  type TraceTarget,
} from "./trace";

// The row the detail pane shows; another run's ids select nothing.
export const useTraceTarget = createMemoryStore<TraceTarget | null>(null);

type Span = { start: number; end: number };

const DEPTH_PADDING = ["pl-0", "pl-4", "pl-8"] as const;

function categoryClassName(alert: Alert): string {
  const category = alertCategory(alert);
  switch (category) {
    case "slow":
      return "text-ds-clementine-600";
    case "context":
      return "text-ds-blueberry-600";
    case "waste":
      return "text-ds-strawberry-600";
    default:
      throw new Error(category satisfies never);
  }
}

function roundClassName(state: RowState): string {
  switch (state) {
    case "running":
      return "animate-pulse bg-ds-blackberry-400";
    case "done":
      return "bg-ds-blackberry-400";
    case "failed":
      return "bg-ds-strawberry-500";
    case "lost":
      return "bg-ds-slate-300";
    default:
      throw new Error(state satisfies never);
  }
}

function callClassName(state: RowState): string {
  switch (state) {
    case "running":
      return "animate-pulse bg-ds-lemon-400";
    case "done":
      return "bg-ds-lemon-400";
    case "failed":
      return "bg-ds-strawberry-500";
    case "lost":
      return "bg-ds-slate-300";
    default:
      throw new Error(state satisfies never);
  }
}

function Bar({
  span,
  scale,
  className,
}: {
  span: Span;
  scale: number;
  className: string;
}): React.ReactElement {
  return (
    <div
      className={cn("absolute top-0 h-full min-w-0.5 rounded-sm", className)}
      style={{
        left: `${String((span.start / scale) * 100)}%`,
        width: `${String(((span.end - span.start) / scale) * 100)}%`,
      }}
    />
  );
}

function AlertBadge({ alerts }: { alerts: Alert[] }): React.ReactElement {
  const first = alerts.at(0);
  if (first === undefined) return <span className="w-6 shrink-0" />;
  return (
    <span
      title={alerts.map((alert) => alertText(alert)).join("\n")}
      className={cn(
        "flex w-6 shrink-0 items-center gap-px",
        categoryClassName(first)
      )}
    >
      <IconAlertTriangle className="size-3" stroke={2} />
      {alerts.length > 1 && <span>{alerts.length}</span>}
    </span>
  );
}

// One line of the tree: label, time track, duration, cost.
function RowCells({
  depth,
  chevron,
  label,
  title,
  alerts,
  track,
  duration,
  cost,
}: {
  depth: 0 | 1 | 2;
  chevron: string | null;
  label: string;
  title: string;
  alerts: Alert[];
  track: React.ReactNode;
  duration: number;
  cost: number | null;
}): React.ReactElement {
  return (
    <>
      <span
        title={title}
        className={cn(
          "flex w-40 shrink-0 items-center gap-1 @3xl:w-56",
          DEPTH_PADDING[depth]
        )}
      >
        {chevron === null ? (
          <span className="size-3 shrink-0" />
        ) : (
          <IconChevronRight
            className={cn("size-3 shrink-0 transition-transform", chevron)}
            stroke={2}
          />
        )}
        <span className="min-w-0 truncate">{label}</span>
      </span>
      <AlertBadge alerts={alerts} />
      <span className="relative h-3 min-w-0 flex-1">{track}</span>
      <span className="w-16 shrink-0 text-right tabular-nums">
        {formatMs(duration)}
      </span>
      <span className="w-14 shrink-0 text-right text-ds-slate-500 tabular-nums">
        {cost === null ? "" : formatCredits(cost)}
      </span>
    </>
  );
}

const ROW_CLASS_NAME =
  "flex w-full cursor-pointer items-center gap-1.5 px-2 py-0.5 text-left font-mono text-xs text-ds-black hover:bg-ds-slate-100";

function isTarget(selected: TraceTarget | null, target: TraceTarget): boolean {
  if (selected === null) return false;
  switch (selected.kind) {
    case "agent":
      return target.kind === "agent" && target.key === selected.key;
    case "round":
    case "call":
      return target.kind === selected.kind && target.id === selected.id;
    default:
      throw new Error(selected satisfies never);
  }
}

function roundTitle(round: TraceRound): string {
  const outcome = round.row.outcome;
  if (outcome === null) return `${round.row.role}, ${round.state}`;
  switch (outcome.kind) {
    case "completed":
      return `${round.row.role}, in ${formatCount(outcome.inputTokens)}, cached ${outcome.cachedInputTokens === null ? "n/a" : formatCount(outcome.cachedInputTokens)}, out ${formatCount(outcome.outputTokens)}, ${outcome.finishReason}`;
    case "failed":
      return `${round.row.role}, failed`;
    default:
      throw new Error(outcome satisfies never);
  }
}

function roundLabel(round: TraceRound): string {
  const outcome = round.row.outcome;
  const head = `round ${String(round.index)}`;
  if (outcome === null) return `${head} ${round.state}`;
  switch (outcome.kind) {
    case "completed": {
      const tools = emittedToolCalls(round.row).map((part) => part.toolName);
      return tools.length === 0
        ? `${head} ${outcome.finishReason}`
        : `${head} → ${tools.join(" ")}`;
    }
    case "failed":
      return `${head} failed`;
    default:
      throw new Error(outcome satisfies never);
  }
}

function CallRow({
  call,
  depth,
  scale,
  flaggedOnly,
  selected,
  onSelect,
}: {
  call: TraceCall;
  depth: 1 | 2;
  scale: number;
  flaggedOnly: boolean;
  selected: TraceTarget | null;
  onSelect: (target: TraceTarget) => void;
}): React.ReactElement | null {
  if (flaggedOnly && call.alerts.length === 0) return null;
  const target = { kind: "call", id: call.row.id } as const;
  return (
    <button
      type="button"
      className={cn(
        ROW_CLASS_NAME,
        isTarget(selected, target) && "bg-ds-moss-100"
      )}
      onClick={() => {
        onSelect(target);
      }}
    >
      <RowCells
        depth={depth}
        chevron={null}
        label={call.row.command}
        title={call.row.command}
        alerts={call.alerts}
        track={
          <Bar
            span={call}
            scale={scale}
            className={callClassName(call.state)}
          />
        }
        duration={call.end - call.start}
        cost={null}
      />
    </button>
  );
}

function AgentTree({
  agent,
  scale,
  flaggedOnly,
  selected,
  onSelect,
}: {
  agent: TraceAgent;
  scale: number;
  flaggedOnly: boolean;
  selected: TraceTarget | null;
  onSelect: (target: TraceTarget) => void;
}): React.ReactElement | null {
  if (flaggedOnly && agentAlerts(agent).length === 0) return null;
  const target = { kind: "agent", key: agent.key } as const;
  return (
    <details open className="group/agent">
      <summary
        className={cn(
          ROW_CLASS_NAME,
          "list-none font-semibold [&::-webkit-details-marker]:hidden",
          isTarget(selected, target) && "bg-ds-moss-100"
        )}
        onClick={() => {
          onSelect(target);
        }}
      >
        <RowCells
          depth={0}
          chevron="group-open/agent:rotate-90"
          label={agent.label}
          title={
            agent.kind === "mission" ? agent.mission.objective : agent.label
          }
          alerts={agent.alerts}
          track={
            <>
              {agent.wait !== null && (
                <Bar
                  span={agent.wait}
                  scale={scale}
                  className="border border-dashed border-ds-slate-400 bg-ds-slate-100"
                />
              )}
              {agent.rounds.map((round) => (
                <Bar
                  key={round.row.id}
                  span={round}
                  scale={scale}
                  className={roundClassName(round.state)}
                />
              ))}
              {[
                ...agent.rounds.flatMap((round) => round.calls),
                ...agent.calls,
              ].map((call) => (
                <Bar
                  key={call.row.id}
                  span={call}
                  scale={scale}
                  className={callClassName(call.state)}
                />
              ))}
            </>
          }
          duration={agent.end - agent.start}
          cost={agent.credits}
        />
      </summary>
      {agent.rounds.map((round) =>
        flaggedOnly && roundAlerts(round).length === 0 ? null : (
          <details key={round.row.id} className="group/round">
            <summary
              className={cn(
                ROW_CLASS_NAME,
                "list-none [&::-webkit-details-marker]:hidden",
                isTarget(selected, { kind: "round", id: round.row.id }) &&
                  "bg-ds-moss-100"
              )}
              onClick={() => {
                onSelect({ kind: "round", id: round.row.id });
              }}
            >
              <RowCells
                depth={1}
                chevron={
                  round.calls.length === 0 ? null : "group-open/round:rotate-90"
                }
                label={roundLabel(round)}
                title={roundTitle(round)}
                alerts={round.alerts}
                track={
                  <Bar
                    span={round}
                    scale={scale}
                    className={roundClassName(round.state)}
                  />
                }
                duration={round.end - round.start}
                cost={round.credits}
              />
            </summary>
            {round.calls.map((call) => (
              <CallRow
                key={call.row.id}
                call={call}
                depth={2}
                scale={scale}
                flaggedOnly={flaggedOnly}
                selected={selected}
                onSelect={onSelect}
              />
            ))}
          </details>
        )
      )}
      {agent.calls.map((call) => (
        <CallRow
          key={call.row.id}
          call={call}
          depth={1}
          scale={scale}
          flaggedOnly={flaggedOnly}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </details>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-ds-slate-500">{label}</dt>
      <dd className="m-0 min-w-0 wrap-anywhere text-ds-black">{children}</dd>
    </div>
  );
}

function AlertList({ alerts }: { alerts: Alert[] }): React.ReactElement | null {
  if (alerts.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {alerts.map((alert, index) => (
        <li
          key={index}
          className={cn("flex items-center gap-1", categoryClassName(alert))}
        >
          <IconAlertTriangle className="size-3 shrink-0" stroke={2} />
          {alertText(alert)}
        </li>
      ))}
    </ul>
  );
}

const PRE_CLASS_NAME =
  "m-0 max-h-96 shrink-0 overflow-auto rounded bg-ds-slate-50 p-2 font-mono text-xs wrap-anywhere whitespace-pre-wrap";

const JSON_TOKEN_CLASS_NAMES = {
  key: "text-ds-blackberry-600",
  string: "text-ds-pine-600",
  number: "text-ds-clementine-600",
  literal: "text-ds-blueberry-600",
  punctuation: "text-ds-slate-500",
  plain: "",
} as const satisfies Record<JsonToken["kind"], string>;

// JSON shows indented and colored, any other text as is.
export function TextBlock({ text }: { text: string }): React.ReactElement {
  // Memoized: a running run re-renders the drawer at each poll.
  const tokens = useMemo(() => jsonTokens(text), [text]);
  return (
    <pre className={PRE_CLASS_NAME}>
      {tokens === null
        ? text
        : tokens.map((token, index) => (
            <span key={index} className={JSON_TOKEN_CLASS_NAMES[token.kind]}>
              {token.text}
            </span>
          ))}
    </pre>
  );
}

function CallOutput({
  conversationId,
  runId,
  call,
}: {
  conversationId: string;
  runId: string;
  call: TraceCall;
}): React.ReactElement {
  const query = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/debug/tool-calls/:callId",
    {
      param: { conversationId, runId, callId: call.row.id },
      staleTime: 0,
      // Until the output lands: the trace can see the call end before this query does.
      refetchInterval: (data) =>
        data !== undefined && data.body.output === null && call.state !== "lost"
          ? 1000
          : false,
    }
  );
  switch (query.state) {
    case "pending":
      return <p className="m-0 text-ds-slate-500">Loading the output…</p>;
    case "error":
      return <p className="m-0 text-ds-strawberry-600">{query.message}</p>;
    case "success":
      return query.body.output === null ? (
        <p className="m-0 text-ds-slate-500">No output yet.</p>
      ) : (
        <TextBlock text={query.body.output} />
      );
    default:
      throw new Error(query satisfies never);
  }
}

// The system and user messages this round sent first in its agent: its earlier rounds sent the others.
function RoundPrompt({
  conversationId,
  runId,
  round,
}: {
  conversationId: string;
  runId: string;
  round: TraceRound;
}): React.ReactElement {
  const query = useApiQuery(
    "/assistant/conversations/:conversationId/runs/:runId/debug/model-calls/:callId",
    {
      param: { conversationId, runId, callId: round.row.id },
      // The row stores its prompt once, with its reservation.
      staleTime: Infinity,
    }
  );
  switch (query.state) {
    case "pending":
      return <p className="m-0 text-ds-slate-500">Loading the prompt…</p>;
    case "error":
      return <p className="m-0 text-ds-strawberry-600">{query.message}</p>;
    case "success": {
      const { prompt } = query.body;
      if (prompt === null)
        return (
          <p className="m-0 text-ds-slate-500">
            Not recorded: the run started without the debug flag.
          </p>
        );
      if (prompt.length === 0)
        return (
          <p className="m-0 text-ds-slate-500">
            No new system or user message: the earlier rounds of this agent sent
            them.
          </p>
        );
      return (
        <>
          {prompt.map((message, index) => (
            <div key={index} className="flex flex-col gap-1">
              <p className="m-0 font-mono text-xs text-ds-slate-500">
                {message.role}
              </p>
              <TextBlock text={message.text} />
            </div>
          ))}
        </>
      );
    }
    default:
      throw new Error(query satisfies never);
  }
}

function RoundDetail({
  conversationId,
  runId,
  round,
}: {
  conversationId: string;
  runId: string;
  round: TraceRound;
}): React.ReactElement {
  const { row } = round;
  const outcome = row.outcome;
  return (
    <>
      <dl className="m-0 flex flex-col gap-0.5">
        <Field label="role">{row.role}</Field>
        <Field label="model">{row.modelId}</Field>
        <Field label="state">{round.state}</Field>
        <Field label="time">
          {`${formatMs(round.start)} → ${formatMs(round.end)} (${formatMs(round.end - round.start)})`}
        </Field>
        <Field label="prompt">{formatBytes(row.promptBytes)}</Field>
        {outcome !== null && outcome.kind === "completed" && (
          <>
            <Field label="tokens in">
              {`${formatCount(outcome.inputTokens)}, cached ${outcome.cachedInputTokens === null ? "n/a" : `${formatCount(outcome.cachedInputTokens)} (${outcome.inputTokens === 0 ? "n/a" : formatPercent(outcome.cachedInputTokens / outcome.inputTokens)})`}`}
            </Field>
            <Field label="tokens out">
              {`${formatCount(outcome.outputTokens)}, reasoning ${outcome.reasoningTokens === null ? "n/a" : formatCount(outcome.reasoningTokens)}`}
            </Field>
            <Field label="cost">{formatCredits(outcome.credits)}</Field>
            <Field label="finish">{outcome.finishReason}</Field>
          </>
        )}
      </dl>
      <AlertList alerts={round.alerts} />
      <table className="w-full border-collapse font-mono text-xs">
        <caption className="text-left font-semibold">
          Prompt composition
        </caption>
        <tbody>
          {COMPOSITION_PARTS.map((part) => (
            <tr key={part}>
              <td className="text-ds-slate-500">{part}</td>
              <td className="text-right tabular-nums">
                {formatBytes(row.composition[part])}
              </td>
            </tr>
          ))}
          <tr>
            <td className="text-ds-slate-500">JSON overhead</td>
            <td className="text-right tabular-nums">
              {formatBytes(compositionOverhead(row))}
            </td>
          </tr>
          <tr>
            <td className="text-ds-slate-500">messages</td>
            <td className="text-right tabular-nums">
              {row.composition.messages}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="m-0 font-semibold">Prompt messages</p>
      <RoundPrompt
        conversationId={conversationId}
        runId={runId}
        round={round}
      />
      {outcome !== null && <p className="m-0 font-semibold">Response</p>}
      {outcome === null ? null : outcome.kind === "failed" ? (
        <pre className={cn(PRE_CLASS_NAME, "bg-ds-strawberry-50")}>
          {outcome.error}
        </pre>
      ) : (
        outcome.content.map((part, index) => {
          switch (part.type) {
            case "text":
              return <TextBlock key={index} text={part.text} />;
            case "tool-call":
              return (
                <div key={index} className="flex flex-col gap-1">
                  <p className="m-0 font-semibold">{`${part.toolName} ${part.toolCallId}`}</p>
                  <TextBlock text={part.input} />
                </div>
              );
            default:
              throw new Error(part satisfies never);
          }
        })
      )}
    </>
  );
}

const PLAN_SOURCES = [
  "juisci",
  "pubmed",
  "papers",
  "guidelines",
  "web",
] as const satisfies (keyof Extract<
  MissionSearch["outcome"],
  { kind: "succeeded" }
>["recall"]["plan"])[];

// A mission's juisci search: its filters, its query plan, each provider request, each step's time and each model call's cost.
function SearchDetail({
  search,
  credits,
}: {
  search: MissionSearch;
  credits: number;
}): React.ReactElement {
  const { filters, outcome } = search;
  const recall = outcome.kind === "recall_failed" ? null : outcome.recall;
  const steps = [
    ...(recall === null
      ? []
      : [
          `plan ${formatMs(recall.planMs)}`,
          `providers ${formatMs(recall.retrievalMs)}`,
          `fusion ${formatMs(recall.fusionMs)}`,
        ]),
    ...(outcome.kind === "succeeded"
      ? [`ranking ${formatMs(outcome.rankingMs)}`]
      : []),
    `total ${formatMs(search.totalMs)}`,
  ];
  return (
    <>
      <dl className="m-0 flex flex-col gap-0.5">
        <Field label="search">{searchOutcomeText(search)}</Field>
        <Field label="query">{search.query}</Field>
        <Field label="objective">{search.objective}</Field>
        <Field label="period">
          {`${filters.publishedAfter === null ? "any" : filters.publishedAfter} → ${filters.publishedBefore === null ? "now" : filters.publishedBefore}`}
        </Field>
        <Field label="families">
          {filters.families === null ? "all" : filters.families.join(", ")}
        </Field>
        <Field label="location">
          {filters.userLocation === null ? "n/a" : filters.userLocation}
        </Field>
        <Field label="clinical use">
          {filters.clinicalUse === null ? "n/a" : filters.clinicalUse}
        </Field>
        <Field label="steps">{steps.join(", ")}</Field>
        {search.models.map((model, index) => (
          <Field key={index} label={model.role}>
            {`${model.model}, in ${formatCount(model.inputTokens)}, out ${formatCount(model.outputTokens)}, ${formatCredits(model.credits)}`}
          </Field>
        ))}
        <Field label="cost">{formatCredits(credits)}</Field>
      </dl>
      {recall !== null && (
        <>
          <table className="w-full border-collapse font-mono text-xs">
            <caption className="text-left font-semibold">Query plan</caption>
            <tbody>
              {PLAN_SOURCES.map((source) => (
                <tr key={source}>
                  <td className="pr-2 align-top text-ds-slate-500">{source}</td>
                  <td className="wrap-anywhere">
                    {recall.plan[source].length === 0
                      ? "none"
                      : recall.plan[source].join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="w-full border-collapse font-mono text-xs">
            <caption className="text-left font-semibold">
              {`Provider requests, ${String(recall.candidates)} candidates after fusion`}
            </caption>
            <tbody>
              {recall.requests.map((request, index) => (
                <tr
                  key={index}
                  className={
                    request.hits === null ? "text-ds-strawberry-600" : ""
                  }
                >
                  <td className="pr-2 align-top text-ds-slate-500">
                    {request.scope === null
                      ? request.source
                      : `${request.source} ${request.scope}`}
                  </td>
                  <td className="pr-2 align-top wrap-anywhere">
                    {request.query}
                  </td>
                  <td className="pr-2 text-right align-top tabular-nums">
                    {request.hits === null ? "failed" : request.hits}
                  </td>
                  <td className="text-right align-top tabular-nums">
                    {formatMs(request.ms)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

type Found =
  | { kind: "agent"; agent: TraceAgent }
  | { kind: "round"; agent: TraceAgent; round: TraceRound }
  | { kind: "call"; agent: TraceAgent; call: TraceCall };

function find(trace: Trace, target: TraceTarget): Found | null {
  for (const agent of trace.agents) {
    switch (target.kind) {
      case "agent":
        if (agent.key === target.key) return { kind: "agent", agent };
        break;
      case "round": {
        const round = agent.rounds.find((item) => item.row.id === target.id);
        if (round !== undefined) return { kind: "round", agent, round };
        break;
      }
      case "call": {
        const call = [
          ...agent.rounds.flatMap((round) => round.calls),
          ...agent.calls,
        ].find((item) => item.row.id === target.id);
        if (call !== undefined) return { kind: "call", agent, call };
        break;
      }
      default:
        throw new Error(target satisfies never);
    }
  }
  return null;
}

function Detail({
  conversationId,
  runId,
  found,
}: {
  conversationId: string;
  runId: string;
  found: Found;
}): React.ReactElement {
  switch (found.kind) {
    case "agent": {
      const { agent } = found;
      return (
        <>
          <p className="m-0 font-semibold">{agent.label}</p>
          <dl className="m-0 flex flex-col gap-0.5">
            <Field label="time">
              {`${formatMs(agent.start)} → ${formatMs(agent.end)} (${formatMs(agent.end - agent.start)})`}
            </Field>
            {agent.wait !== null && (
              <Field label="before round 1">
                {formatMs(agent.wait.end - agent.wait.start)}
              </Field>
            )}
            <Field label="rounds">{agent.rounds.length}</Field>
            <Field label="model time">{formatMs(agent.modelMs)}</Field>
            <Field label="tool time">{formatMs(agent.toolMs)}</Field>
            <Field label="cost">{formatCredits(agent.credits)}</Field>
            {agent.kind === "mission" && (
              <>
                <Field label="status">{agent.mission.status}</Field>
                <Field label="query">
                  {agent.mission.query === null ? "n/a" : agent.mission.query}
                </Field>
                <Field label="objective">{agent.mission.objective}</Field>
              </>
            )}
          </dl>
          <AlertList alerts={agent.alerts} />
        </>
      );
    }
    case "round":
      return (
        <>
          <p className="m-0 font-semibold">{`${found.agent.label}, round ${String(found.round.index)}`}</p>
          <RoundDetail
            conversationId={conversationId}
            runId={runId}
            round={found.round}
          />
        </>
      );
    case "call": {
      const { call } = found;
      return (
        <>
          <p className="m-0 font-semibold">{`${found.agent.label}, tool call`}</p>
          <dl className="m-0 flex flex-col gap-0.5">
            <Field label="state">{call.state}</Field>
            <Field label="time">
              {`${formatMs(call.start)} → ${formatMs(call.end)} (${formatMs(call.end - call.start)})`}
            </Field>
            <Field label="exit code">
              {call.row.exitCode === null ? "n/a" : call.row.exitCode}
            </Field>
            <Field label="output">
              {call.row.outputChars === null
                ? "n/a"
                : `${formatCount(call.row.outputChars)} chars`}
            </Field>
            <Field label="source">
              {call.row.step === null
                ? "no"
                : `${call.row.step.tool}: ${call.row.step.query}`}
            </Field>
            <Field label="tool call id">{call.row.toolCallId}</Field>
          </dl>
          <AlertList alerts={call.alerts} />
          {call.row.search !== null && (
            <SearchDetail
              search={call.row.search}
              credits={callCredits(call.row)}
            />
          )}
          <pre className={PRE_CLASS_NAME}>{call.row.command}</pre>
          <CallOutput
            conversationId={conversationId}
            runId={runId}
            call={call}
          />
        </>
      );
    }
    default:
      throw new Error(found satisfies never);
  }
}

// Labels need room: the ends always, the middle, then the quarters as the track widens.
const TICKS = [
  { at: 0, className: "" },
  { at: 0.25, className: "hidden -translate-x-1/2 @5xl:block" },
  { at: 0.5, className: "hidden -translate-x-1/2 @2xl:block" },
  { at: 0.75, className: "hidden -translate-x-1/2 @5xl:block" },
  { at: 1, className: "-translate-x-full" },
] as const;

export function TraceTab({
  conversationId,
  debug,
  trace,
  flaggedOnly,
}: {
  conversationId: string;
  debug: RunDebug;
  trace: Trace;
  flaggedOnly: boolean;
}): React.ReactElement {
  const [selected, setSelected] = useTraceTarget();
  const scale = Math.max(
    1,
    trace.end,
    ...trace.agents.map((agent) => agent.end)
  );
  const found = selected === null ? null : find(trace, selected);
  return (
    <div className="flex min-h-0 flex-1 flex-col-reverse @3xl:flex-row">
      <div className="@container flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto pb-4">
        {debug.failure !== null && (
          <pre className={cn(PRE_CLASS_NAME, "m-2 bg-ds-strawberry-50")}>
            {debug.failure}
          </pre>
        )}
        {flaggedOnly && (
          <ul className="m-0 flex list-none flex-col gap-0.5 border-b border-ds-slate-200 p-2 font-mono text-xs">
            {trace.alerts.length === 0 && (
              <li className="text-ds-slate-500">No alert.</li>
            )}
            {trace.alerts.map((item, index) => (
              <li key={index}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-1 text-left hover:underline",
                    categoryClassName(item.alert)
                  )}
                  onClick={() => {
                    setSelected(item.target);
                  }}
                >
                  <IconAlertTriangle className="size-3 shrink-0" stroke={2} />
                  {`${item.where}: ${alertText(item.alert)}`}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="sticky top-0 z-10 flex items-end gap-1.5 border-b border-ds-slate-200 bg-ds-cream px-2 pt-1 font-mono text-xs text-ds-slate-500">
          <span className="w-40 shrink-0 @3xl:w-56">agent</span>
          <span className="w-6 shrink-0" />
          <span className="relative h-8 min-w-0 flex-1">
            {trace.markers.map((marker, index) => (
              <span
                key={index}
                title={`${marker.kind} at ${formatMs(marker.at)}`}
                className="absolute top-0 h-3 w-px bg-ds-pine-600"
                style={{ left: `${String((marker.at / scale) * 100)}%` }}
              />
            ))}
            {TICKS.map((tick) => (
              <span
                key={tick.at}
                className="absolute bottom-0 h-1 w-px bg-ds-slate-300"
                style={{ left: `${String(tick.at * 100)}%` }}
              />
            ))}
            {TICKS.map((tick) => (
              <span
                key={tick.at}
                className={cn(
                  "absolute bottom-1 whitespace-nowrap tabular-nums",
                  tick.className
                )}
                style={{ left: `${String(tick.at * 100)}%` }}
              >
                {formatMs(scale * tick.at)}
              </span>
            ))}
          </span>
          <span className="w-16 shrink-0 text-right">time</span>
          <span className="w-14 shrink-0 text-right">cost</span>
        </div>
        <p className="m-0 px-2 py-1 font-mono text-xs text-ds-slate-500">
          {trace.markers
            .map((marker) => `${marker.kind} ${formatMs(marker.at)}`)
            .join(" · ")}
        </p>
        {trace.agents.map((agent) => (
          <AgentTree
            key={agent.key}
            agent={agent}
            scale={scale}
            flaggedOnly={flaggedOnly}
            selected={selected}
            onSelect={setSelected}
          />
        ))}
      </div>
      <section
        aria-label="Detail"
        className="flex max-h-64 shrink-0 flex-col gap-2 overflow-y-auto border-b border-ds-slate-200 bg-ds-white p-3 text-xs @3xl:max-h-none @3xl:w-2/5 @3xl:border-b-0 @3xl:border-l"
      >
        {found === null ? (
          <p className="m-0 text-ds-slate-500">Select a row.</p>
        ) : (
          <Detail
            conversationId={conversationId}
            runId={debug.run.id}
            found={found}
          />
        )}
      </section>
    </div>
  );
}
